import {
  DocumentRequestReason,
  SellerVerificationAction,
} from "@/Apollo/schema";

/**
 * Groups server-classified verification issues by the CTA that resolves them.
 *
 * The gateway names the fix per issue (`action`) and carries the button copy
 * (`cta_label`, localised server-side); this module only decides which
 * surface a click opens. Grouping is by action so two issues with the same
 * fix share one button instead of rendering interchangeable twins — the
 * confusion WHO-5241 exists to kill.
 *
 * Copy vs classification are deliberately decoupled: the gateway's t() falls
 * back to returning the raw key on a Lokalise miss, and a missing key must
 * only cost the text it names — never rewire which button a blocked seller
 * gets. The two key namespaces are pinned here, once, mirroring the
 * gateway's seller-verification service and whoppah-android's
 * StripeAccountDto normalisation.
 */

export type VerificationIssue = {
  code: string;
  action: SellerVerificationAction;
  title: string;
  description: string;
  cta_label: string;
  /** The one-line why (spec v3); older gateways do not send it. */
  why?: string | null;
  /** Stripe's error codes behind the issue, for copy per reason. Never shown raw. */
  reason_codes?: readonly string[] | null;
  document_request_reason?: DocumentRequestReason | null;
};

/** A renderable issue row — copy that survived the raw-key guard. */
export type VerificationIssueRow = {
  code: string;
  title: string;
  description: string | null;
  why: string | null;
  reasonCodes: readonly string[];
  documentRequestReason: DocumentRequestReason | null;
};

/** Which surface resolves a given action. */
export type VerificationCta =
  | { kind: "stripe-link"; linkType: "ACCOUNT_ONBOARDING" | "ACCOUNT_UPDATE" }
  | { kind: "bank-form" }
  | { kind: "none" };

export type VerificationCtaGroup = {
  action: SellerVerificationAction;
  cta: VerificationCta;
  /** Gateway button copy; null when it missed — render the generic label. */
  ctaLabel: string | null;
  /** Rows whose copy is renderable; may be empty while the button stays. */
  issues: VerificationIssueRow[];
};

const RAW_ISSUE_KEY_PREFIX = "verification-issue-";
const RAW_ACTION_KEY_PREFIX = "verification-action-";

const localizedOrNull = (value: string, rawKeyPrefix: string): string | null =>
  !value.trim() || value.startsWith(rawKeyPrefix) ? null : value;

const ACTION_CTA: Record<SellerVerificationAction, VerificationCta> = {
  // Correcting already-submitted data — Stripe's update flow edits it.
  // Re-running onboarding would push the same pointless re-uploads Stripe's
  // own prompts do.
  [SellerVerificationAction.FixTypedDetails]: {
    kind: "stripe-link",
    linkType: "ACCOUNT_UPDATE",
  },
  [SellerVerificationAction.UploadDocument]: {
    kind: "stripe-link",
    linkType: "ACCOUNT_UPDATE",
  },
  // Data never provided (e.g. company owners) — onboarding collects what is
  // currently due.
  [SellerVerificationAction.CompleteOnboarding]: {
    kind: "stripe-link",
    linkType: "ACCOUNT_ONBOARDING",
  },
  // The IBAN form lives on this very page, right above the Stripe section.
  [SellerVerificationAction.AddBankAccount]: { kind: "bank-form" },
  // We are the ones fixing it; a button here would be worse than none. The
  // issue's own copy already tells the seller no action is needed.
  [SellerVerificationAction.NonePlatformFixing]: { kind: "none" },
};

// Onboarding collects whatever Stripe says is due, so it is the safe surface
// for an action this build has never heard of (the gateway normalises unknown
// actions the same way before they ever reach us).
const FALLBACK_CTA: VerificationCta = {
  kind: "stripe-link",
  linkType: "ACCOUNT_ONBOARDING",
};

export const groupIssuesByAction = (
  issues: VerificationIssue[]
): VerificationCtaGroup[] => {
  // Map preserves insertion order — the gateway sends issues most-severe
  // first, and the first group keeps that rank.
  const groups = new Map<SellerVerificationAction, VerificationIssue[]>();

  for (const issue of issues) {
    const siblings = groups.get(issue.action);
    if (siblings) {
      siblings.push(issue);
    } else {
      groups.set(issue.action, [issue]);
    }
  }

  return [...groups.entries()].map(([action, siblings]) => {
    const rows: VerificationIssueRow[] = [];
    for (const issue of siblings) {
      const title = localizedOrNull(issue.title, RAW_ISSUE_KEY_PREFIX);
      if (title === null) {
        // The classified action stays valid data — only this row's text is
        // unrenderable. Loud, so a missing Lokalise key shows up somewhere.
        console.warn(
          `Dropping unrenderable copy of verification issue ${issue.code} (raw i18n key from the gateway)`
        );
        continue;
      }
      rows.push({
        code: issue.code,
        title,
        description: localizedOrNull(issue.description, RAW_ISSUE_KEY_PREFIX),
        why: issue.why
          ? localizedOrNull(issue.why, RAW_ISSUE_KEY_PREFIX)
          : null,
        reasonCodes: issue.reason_codes ?? [],
        documentRequestReason: issue.document_request_reason ?? null,
      });
    }

    return {
      action,
      cta: ACTION_CTA[action] ?? FALLBACK_CTA,
      // First usable label among the siblings — the first issue alone could
      // carry the one label that missed Lokalise.
      ctaLabel:
        siblings
          .map(issue => localizedOrNull(issue.cta_label, RAW_ACTION_KEY_PREFIX))
          .find(label => label !== null) ?? null,
      issues: rows,
    };
  });
};
